import { useChatStatus } from '@ai-sdk-tools/store';
import type { ChatStatus } from 'ai';
import { useEffect, useRef } from 'react';

import { normalizeChatUuid } from '../lib/notification-helpers';

export interface UseAutoResumeParams {
  /**
   * Whether auto-resume is enabled.
   * When true, the hook will automatically resume interrupted streams.
   */
  autoResume: boolean;
  /**
   * The active chat id. Existing chats probe the backend stream endpoint on
   * activation instead of inferring resumability from stale local state.
   */
  chatUuid: string;
  /**
   * Brand-new optimistic chats do not have a server stream to reconnect to yet.
   */
  isNewChat: boolean;
  /**
   * Backend stream id for the active stream, if known. This keeps 204
   * deduping scoped to a specific stream when the server reports one.
   */
  activeStreamId?: string | null;
  /**
   * Function to resume the stream.
   * Should be called when auto-resume is enabled for an existing chat activation.
   */
  resumeStream: () => void;
  /**
   * Called immediately before a resume probe starts.
   * Used by the transport layer to mark 204 responses as unavailable.
   */
  onResumeAttempt?: (attemptKey: string) => void;
}

const unavailableAutoResumeAttemptKeys = new Set<string>();

export function markAutoResumeAttemptUnavailable(attemptKey: string): void {
  unavailableAutoResumeAttemptKeys.add(attemptKey);
}

export function clearUnavailableAutoResumeAttempt(attemptKey: string): void {
  unavailableAutoResumeAttemptKeys.delete(attemptKey);
}

export function clearUnavailableAutoResumeAttempts(): void {
  unavailableAutoResumeAttemptKeys.clear();
}

export function isAutoResumeAttemptUnavailable(attemptKey: string): boolean {
  return unavailableAutoResumeAttemptKeys.has(attemptKey);
}

export function shouldAutoResume({
  autoResume,
  isNewChat,
  status,
}: {
  autoResume: boolean;
  isNewChat: boolean;
  status: ChatStatus;
}): boolean {
  if (!autoResume || isNewChat) {
    return false;
  }

  if (status !== 'ready') {
    return false;
  }

  return true;
}

export function getAutoResumeAttemptKey({
  activeStreamId,
  chatUuid,
  isNewChat,
}: {
  activeStreamId?: string | null;
  chatUuid: string;
  isNewChat: boolean;
}): string | null {
  if (isNewChat) {
    return null;
  }

  if (activeStreamId) {
    return `stream:${activeStreamId}`;
  }

  return `chat:${normalizeChatUuid(chatUuid)}`;
}

/**
 * Hook that automatically resumes interrupted chat streams.
 * Based on the Vercel AI Chatbot implementation: https://github.com/vercel/ai-chatbot/blob/d0b6f37aeee532b942245ee9d406312f79e2fabe/hooks/use-auto-resume.ts
 *
 * When autoResume is enabled for an existing chat, this hook probes the
 * backend stream endpoint once per activation and lets the server decide
 * whether there is a live stream to resume.
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const chat = useChat({ resume: true, ... });
 *   const { resumeStream } = chat; // or from useChatActions
 *
 *   useAutoResume({
 *     autoResume: true,
 *     chatUuid,
 *     isNewChat,
 *     resumeStream,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAutoResume({
  activeStreamId,
  autoResume,
  chatUuid,
  isNewChat,
  onResumeAttempt,
  resumeStream,
}: UseAutoResumeParams) {
  const status = useChatStatus();
  const attemptedResumeKeyRef = useRef<string | null>(null);

  const attemptKey = getAutoResumeAttemptKey({
    activeStreamId,
    chatUuid,
    isNewChat,
  });

  useEffect(() => {
    if (status === 'submitted' || status === 'streaming') {
      clearUnavailableAutoResumeAttempt(`chat:${normalizeChatUuid(chatUuid)}`);
    }
  }, [chatUuid, status]);

  useEffect(() => {
    if (
      !shouldAutoResume({
        autoResume,
        isNewChat,
        status,
      })
    ) {
      return;
    }

    if (!attemptKey || attemptedResumeKeyRef.current === attemptKey || isAutoResumeAttemptUnavailable(attemptKey)) {
      return;
    }

    attemptedResumeKeyRef.current = attemptKey;
    onResumeAttempt?.(attemptKey);
    resumeStream();
  }, [attemptKey, autoResume, isNewChat, onResumeAttempt, resumeStream, status]);
}
